import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { slugify } from "../lib/content";

type MarkdownArticleProps = {
  markdown: string;
};

function textFromChildren(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textFromChildren).join("");
  if (children && typeof children === "object" && "props" in children) {
    const child = children as { props?: { children?: ReactNode } };
    return textFromChildren(child.props?.children);
  }
  return "";
}

export function MarkdownArticle({ markdown }: MarkdownArticleProps) {
  return (
    <article className="markdown-article" data-reader-article>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => {
            const id = slugify(textFromChildren(children));
            return <h2 id={id}>{children}</h2>;
          },
          h3: ({ children }) => {
            const id = slugify(textFromChildren(children));
            return <h3 id={id}>{children}</h3>;
          },
          a: ({ href = "", children }) => {
            const external = /^https?:\/\//.test(href);
            return (
              <a
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
              >
                {children}
              </a>
            );
          },
          img: ({ src = "", alt = "" }) => {
            const resolved = /^https?:\/\//.test(src)
              ? src
              : `${import.meta.env.BASE_URL}${src.replace(/^\.\//, "")}`;
            return (
              <figure>
                <img src={resolved} alt={alt} loading="lazy" />
                {alt && <figcaption>{alt}</figcaption>}
              </figure>
            );
          },
          table: ({ children }) => (
            <div className="table-scroll">
              <table>{children}</table>
            </div>
          ),
          code: ({ className, children }) => {
            const inline = !className && !String(children).includes("\n");
            return inline ? (
              <code>{children}</code>
            ) : (
              <code className={className}>{children}</code>
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </article>
  );
}
